
import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth, signInWithEmail, signUpWithEmail } from '../../hooks/useAuth';
import Header from '../../components/feature/Header';
import Footer from '../../components/feature/Footer';

type Step = 'credentials' | 'username';

export default function LoginPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  const [mode, setMode] = useState<'login' | 'signup'>('login');
  const [step, setStep] = useState<Step>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [username, setUsername] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const from = (location.state as { from?: string })?.from || '/';

  // 既にログイン済みならリダイレクト
  useEffect(() => {
    if (!authLoading && user) {
      navigate(from, { replace: true });
    }
  }, [user, authLoading, navigate, from]);

  const resetForm = () => {
    setEmail('');
    setPassword('');
    setUsername('');
    setError('');
    setStep('credentials');
  };

  const handleCredentialsNext = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError('メールアドレスとパスワードを入力してください');
      return;
    }
    if (password.length < 6) {
      setError('パスワードは6文字以上で入力してください');
      return;
    }
    setError('');

    if (mode === 'login') {
      setLoading(true);
      try {
        const { data, error: err } = await signInWithEmail(email, password);
        if (err || !data) {
          setError('メールアドレスまたはパスワードが正しくありません');
        } else {
          // ログイン成功 → リダイレクト
          navigate(from, { replace: true });
        }
      } catch {
        setError('エラーが発生しました。もう一度お試しください');
      } finally {
        setLoading(false);
      }
    } else {
      setStep('username');
    }
  };

  const handleSignup = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = username.trim();
    if (!trimmed) { setError('ユーザーネームを入力してください'); return; }
    if (trimmed.length < 3) { setError('3文字以上で入力してください'); return; }
    if (trimmed.length > 20) { setError('20文字以内で入力してください'); return; }
    if (!/^[a-zA-Z0-9_\u3040-\u309F\u30A0-\u30FF\u4E00-\u9FFF]+$/.test(trimmed)) {
      setError('英数字・日本語・アンダースコア（_）のみ使用できます');
      return;
    }
    setError('');
    setLoading(true);
    try {
      const { data, error: err } = await signUpWithEmail(email, password, trimmed);
      if (err || !data) {
        const msg = (err as Error)?.message || '';
        if (msg.includes('already registered') || msg.includes('User already registered')) {
          setError('このメールアドレスは既に登録されています');
          setStep('credentials');
        } else if (msg.includes('ユーザーネームは既に')) {
          setError('このユーザーネームは既に使用されています。別の名前をお試しください');
        } else {
          setError('登録に失敗しました。もう一度お試しください');
        }
      } else {
        navigate(from, { replace: true });
      }
    } catch {
      setError('エラーが発生しました。もう一度お試しください');
    } finally {
      setLoading(false);
    }
  };

  // 認証確認中
  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-teal-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-sm text-gray-500 dark:text-gray-400">読み込み中...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex flex-col bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800">
      <Header />

      <main className="flex-1 flex items-center justify-center px-4 py-16">
        <div className="max-w-md w-full">
          <div className="text-center mb-8">
            <div className="w-16 h-16 flex items-center justify-center bg-gradient-to-br from-teal-500 to-emerald-600 rounded-2xl mx-auto mb-4 shadow-lg">
              <i className={`ri-${step === 'username' ? 'user-smile-line' : 'shield-user-line'} text-3xl text-white`}></i>
            </div>
            <h1 className="text-2xl font-bold text-gray-800 dark:text-white mb-2">
              {mode === 'login' ? 'ログイン' : step === 'credentials' ? 'アカウント作成' : 'ユーザーネームを設定'}
            </h1>
            <p className="text-sm text-gray-600 dark:text-gray-400">
              {mode === 'login'
                ? 'このサイトを利用するには認証が必要です'
                : step === 'credentials'
                ? 'メールアドレスとパスワードを入力してください'
                : 'あなたの表示名を決めてください（後から何度でも変更できます）'}
            </p>
          </div>

          <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-xl p-8">
            {/* ステップインジケーター（新規登録時） */}
            {mode === 'signup' && (
              <div className="flex items-center justify-center gap-3 mb-6">
                <div className="flex items-center gap-1.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step === 'credentials' ? 'bg-teal-500 text-white' : 'bg-teal-100 text-teal-600'}`}>
                    {step === 'username' ? <i className="ri-check-line"></i> : '1'}
                  </div>
                  <span className={`text-xs font-medium ${step === 'credentials' ? 'text-teal-600' : 'text-gray-400'}`}>アカウント情報</span>
                </div>
                <div className={`w-8 h-0.5 transition-all ${step === 'username' ? 'bg-teal-400' : 'bg-gray-200'}`}></div>
                <div className="flex items-center gap-1.5">
                  <div className={`w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold transition-all ${step === 'username' ? 'bg-teal-500 text-white' : 'bg-gray-100 text-gray-400'}`}>
                    2
                  </div>
                  <span className={`text-xs font-medium ${step === 'username' ? 'text-teal-600' : 'text-gray-400'}`}>ユーザーネーム</span>
                </div>
              </div>
            )}

            {/* タブ切り替え */}
            {step === 'credentials' && (
              <div className="flex bg-gray-100 dark:bg-gray-800 rounded-full p-1 mb-6">
                <button
                  type="button"
                  onClick={() => { setMode('login'); resetForm(); }}
                  className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    mode === 'login' ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  ログイン
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('signup'); resetForm(); }}
                  className={`flex-1 py-2.5 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                    mode === 'signup' ? 'bg-white dark:bg-gray-700 text-gray-800 dark:text-white shadow-sm' : 'text-gray-500 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                >
                  新規登録
                </button>
              </div>
            )}

            {/* エラー */}
            {error && (
              <div className="bg-red-50 border border-red-200 text-red-700 text-sm rounded-lg px-4 py-3 mb-6 flex items-start">
                <i className="ri-error-warning-line text-lg mr-2 flex-shrink-0 mt-0.5"></i>
                <span>{error}</span>
              </div>
            )}

            {/* ステップ1: メール・パスワード */}
            {step === 'credentials' && (
              <form onSubmit={handleCredentialsNext} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">メールアドレス</label>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder="example@email.com"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:text-white transition-all"
                    autoFocus
                    autoComplete="email"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">パスワード</label>
                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="6文字以上"
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:text-white transition-all"
                    autoComplete={mode === 'login' ? 'current-password' : 'new-password'}
                  />
                </div>
                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-bold hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>処理中...
                    </span>
                  ) : mode === 'login' ? 'ログイン' : '次へ →'}
                </button>
              </form>
            )}

            {/* ステップ2: ユーザーネーム */}
            {step === 'username' && (
              <form onSubmit={handleSignup} className="space-y-5">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">ユーザーネーム</label>
                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="例: nagayama_study"
                    maxLength={20}
                    className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-teal-500 focus:border-transparent bg-white dark:bg-gray-800 dark:text-white transition-all"
                    autoFocus
                  />
                  <div className="flex justify-between mt-2">
                    <p className="text-xs text-gray-400">3〜20文字。英数字・日本語・アンダースコア（_）が使用できます</p>
                    <span className="text-xs text-gray-400 flex-shrink-0 ml-2">{username.length}/20</span>
                  </div>
                </div>

                <div className="bg-teal-50 dark:bg-teal-900/20 rounded-lg px-4 py-3 text-xs text-teal-700 dark:text-teal-300 flex items-start gap-2">
                  <i className="ri-information-line text-base flex-shrink-0 mt-0.5"></i>
                  <span>このユーザーネームはランキングや他のユーザーへの表示に使われます。後から何度でも変更できます。</span>
                </div>

                <button
                  type="submit"
                  disabled={loading}
                  className="w-full py-3.5 bg-gradient-to-r from-teal-500 to-emerald-600 text-white rounded-lg text-sm font-bold hover:from-teal-600 hover:to-emerald-700 transition-all cursor-pointer whitespace-nowrap disabled:opacity-50 disabled:cursor-not-allowed shadow-lg hover:shadow-xl"
                >
                  {loading ? (
                    <span className="flex items-center justify-center gap-2">
                      <i className="ri-loader-4-line animate-spin"></i>アカウントを作成中...
                    </span>
                  ) : 'アカウントを作成'}
                </button>

                <button
                  type="button"
                  onClick={() => { setStep('credentials'); setError(''); }}
                  className="w-full py-2 text-gray-500 text-sm font-medium hover:text-gray-700 dark:hover:text-gray-300 transition-colors cursor-pointer whitespace-nowrap flex items-center justify-center gap-1"
                >
                  <i className="ri-arrow-left-line"></i>戻る
                </button>
              </form>
            )}

            {mode === 'login' && step === 'credentials' && (
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-gray-700">
                <div className="text-xs text-gray-500 dark:text-gray-400 space-y-2">
                  <p className="flex items-start">
                    <i className="ri-information-line mr-2 flex-shrink-0 mt-0.5"></i>
                    <span>Admin権限は特定のメールアドレスに自動的に付与されます</span>
                  </p>
                  <p className="flex items-start">
                    <i className="ri-eye-line mr-2 flex-shrink-0 mt-0.5"></i>
                    <span>Viewer権限では研究ポートフォリオの閲覧のみ可能です</span>
                  </p>
                </div>
              </div>
            )}
          </div>

          <div className="text-center mt-6">
            <button
              type="button"
              onClick={() => navigate('/')}
              className="text-sm text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200 transition-colors cursor-pointer whitespace-nowrap inline-flex items-center gap-1"
            >
              <i className="ri-arrow-left-line"></i>ホームに戻る
            </button>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}
